import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, Container, LinearProgress, Link, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { CheckCircleOutline as YesIcon, RemoveCircleOutline as NoIcon } from '@mui/icons-material';
import { plansApi } from '../../services/api';
import { PlanState } from '../../types';
import { errorMessage } from '../../utils/errors';
import { formatDateTime } from '../../utils/format';

type Offer = PlanState['plans'][number];

const yes = <YesIcon color="success" fontSize="small" aria-label="Included" />;
const no = <NoIcon color="disabled" fontSize="small" aria-label="Not included" />;

const FEATURES: { label: string; cell: (plan: Offer) => React.ReactNode }[] = [
  { label: 'Job board with filters and saved searches', cell: () => yes },
  { label: 'Filling applications in the browser', cell: () => yes },
  { label: 'Tracking the applications you made', cell: () => yes },
  { label: 'Tailored resumes', cell: (plan) => (plan.tailored_per_month > 0 ? `${plan.tailored_per_month} a month` : no) },
  { label: 'How you match each job: the skills it names that you have, and the gaps', cell: (plan) => (plan.job_match ? yes : no) },
  { label: 'Application status from employer email', cell: (plan) => (plan.email_tracking ? yes : no) },
];

const dollars = (amount: string): string => `$${Number(amount) % 1 === 0 ? Number(amount).toFixed(0) : Number(amount).toFixed(2)}`;

/** "Pro is $12 a month, or $120 a year (17% less)." with whatever is set. */
function priceLine(state: PlanState): string {
  const { monthly, yearly, yearly_saving_percent: saving } = state.prices;
  if (monthly && yearly) return `Pro is ${dollars(monthly)} a month, or ${dollars(yearly)} a year${saving ? ` (${saving}% less)` : ''}.`;
  if (monthly) return `Pro is ${dollars(monthly)} a month.`;
  if (yearly) return `Pro is ${dollars(yearly)} a year.`;
  return 'Monthly, or yearly at a discount.';
}

/**
 * The plan the person is on, what it allows and how much of it they have
 * used, and what the other plan offers. Payments are not wired yet: the
 * upgrade button says so.
 */
const PlanPage: React.FC = () => {
  const [state, setState] = useState<PlanState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    plansApi
      .current(controller.signal)
      .then(setState)
      .catch((e) => {
        if (!controller.signal.aborted) setError(errorMessage(e, 'Could not load your plan.'));
      });
    return () => controller.abort();
  }, []);

  const used = state?.usage.tailored_this_month ?? 0;
  const limit = state?.limits.tailored_per_month ?? 0;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Plan
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        What your plan includes and how much of it you have used.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!state && !error && <Skeleton variant="rounded" height={160} />}
      {state && (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5, mb: 1 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                  {state.label}
                </Typography>
                {state.trial && state.valid_until && <Chip size="small" color="primary" label={`Trial ends ${formatDateTime(state.valid_until)}`} />}
                {state.plan === 'pro' && state.valid_until && <Chip size="small" color="primary" label={`Renews ${formatDateTime(state.valid_until)}`} />}
              </Stack>
              {state.lapsed && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Your {state.lapsed_from === 'trial' ? 'trial' : 'Pro plan'} has ended, so you are on the Free plan. Tailored resumes and
                  email tracking are paused; what you built and tracked is still here.
                </Alert>
              )}
              {limit > 0 ? (
                <Box sx={{ maxWidth: 420 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Tailored resumes: {used} of {limit} {state.trial ? 'in the trial' : 'this month'}
                  </Typography>
                  <LinearProgress variant="determinate" value={Math.min(100, (used / limit) * 100)} aria-label="Tailored resumes used" sx={{ height: 8, borderRadius: 4 }} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Tailored resumes, how you match each job, and email tracking are part of Pro.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
                Free and Pro
              </Typography>
              <Table size="small" aria-label="What each plan includes">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {state.plans.map((plan) => (
                      <TableCell key={plan.key} align="center" sx={{ fontWeight: 700 }}>
                        {plan.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {FEATURES.map((feature) => (
                    <TableRow key={feature.label}>
                      <TableCell>{feature.label}</TableCell>
                      {state.plans.map((plan) => (
                        <TableCell key={plan.key} align="center">
                          {feature.cell(plan)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
                <Button variant="contained" disabled>
                  Upgrade to Pro
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {priceLine(state)} Payments are being set up; every new account starts with a {state.trial_offer.days}-day
                  Pro trial.
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Typography variant="caption" color="text.secondary">
            Questions about plans: <Link href="mailto:support@jobflo.me">support@jobflo.me</Link>. See the{' '}
            <Link component={RouterLink} to="/terms">
              Terms of Service
            </Link>{' '}
            for billing and cancellation.
          </Typography>
        </Stack>
      )}
    </Container>
  );
};

export default PlanPage;
